import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IpponButton,
  IpponCard,
  IpponHSpace,
  IpponHSpaceSlot,
  IpponIconTile,
  IpponText,
  IpponTitle,
  IpponVSpace,
} from '@ippon-ui/react';
import { MarkdownContent, MarkdownRenderer, type RenderedHtml } from '@/generic/markdown';
import { Optional } from '@/generic/functional';
import { useDialog, useOptional } from '@/generic/react-tools';
import type { DiagnosticReport } from '@/diagnostic/domain/DiagnosticReport.ts';

const renderer = new MarkdownRenderer();

const useRenderedReport = (report: DiagnosticReport, open: boolean): Optional<RenderedHtml> =>
  useMemo(() => {
    if (!open) {
      return Optional.empty();
    }
    const content = MarkdownContent.of(report.content);
    return Optional.of(renderer.render(content));
  }, [report, open]);

const DiagnosticReportBodyContent = ({ rendered }: { rendered: Optional<RenderedHtml> }) =>
  useOptional({
    optional: rendered,
    onValuated: (html) => (
      <IpponCard border={true}>
        <div className="ippon-v-space -gap-8" dangerouslySetInnerHTML={{ __html: html.value }} />
      </IpponCard>
    ),
  });

export const DiagnosticReportModalComponent = ({
  open,
  onClose,
  report,
}: {
  open: boolean;
  onClose: () => void;
  report: DiagnosticReport;
}) => {
  const { t } = useTranslation('diagnostic');
  const dialog = useDialog(open);
  const rendered = useRenderedReport(report, open);

  const download = () => {
    const file = report.file;
    const url = URL.createObjectURL(file);
    const link = document.createElement('a');
    link.href = url;
    link.download = file.name;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  return (
    <dialog className="ippon-modal" ref={dialog} data-selector="diagnostic.report-modal">
      <div className="ippon-modal--header">
        <IpponHSpace align="middle" gap={16}>
          <IpponIconTile icon={{ name: 'document-text', variant: 'outline' }} size="small" />
          <IpponHSpaceSlot expand={true}>
            <IpponVSpace>
              <hgroup>
                <IpponTitle tag="h2" level={5}>
                  {t('diagnostic.report.title')}
                </IpponTitle>
                <IpponText tag="p" variant="body" color="neutral-tertiary-inversed">
                  {t('diagnostic.report.subtitle')}
                </IpponText>
              </hgroup>
            </IpponVSpace>
          </IpponHSpaceSlot>
          <IpponButton
            color="neutral"
            variant="text"
            onClick={onClose}
            iconLeft={{ name: 'close' }}
            dataSelector="diagnostic.report-modal.close"
          />
        </IpponHSpace>
      </div>
      <hr className="ippon-modal--separator" />
      <div className="ippon-modal--body" data-selector="diagnostic.report-modal.body">
        <DiagnosticReportBodyContent rendered={rendered} />
      </div>
      <div className="ippon-modal--footer">
        <IpponButton
          color="neutral"
          variant="outline"
          size="small"
          onClick={download}
          iconRight={{ name: 'download', variant: 'outline' }}
          dataSelector="diagnostic.download-report"
        >
          {t('diagnostic.download-report')}
        </IpponButton>
      </div>
    </dialog>
  );
};
